import { useState } from "react";
import { Box, Grid, Typography } from "@mui/material";

import FileDropzone from "components/FileDropzone";
import useToaster from "hooks/useToaster";
import {
  downloadTemplateFile,
  fetchVendorWorkingHours,
  saveVendorWorkingHours,
  uploadVendorWorkingHoursFile,
} from "services/eddService";
import {
  EDD_TEMPLATE_NAMES,
  UPLOAD_FORMAT_HINT,
} from "constants/eddConfig";
import {
  buildWeeklySchedule,
  getChangedScheduleRows,
  getRowResultToast,
  getWeeklyTotalHours,
  validateScheduleRow,
} from "utils/eddUtils";
import EddSectionCard from "../components/EddSectionCard";
import VendorSearch from "../VendorSearch";
import VendorInfoCard from "./components/VendorInfoCard";
import WeeklySchedule from "./components/WeeklySchedule";
import RowResultDialog from "../RowResultDialog";
import { useEddUploadContext } from "../EddUploadContext";

const VendorWorkingHours = () => {
  const triggerToaster = useToaster();

  const [vendor, setVendor] = useState(null);
  const [schedule, setSchedule] = useState([]);
  const [savedSchedule, setSavedSchedule] = useState([]);
  const [isScheduleLoading, setIsScheduleLoading] = useState(false);
  const [isSaving, setIsSaving] = useState(false);
  const [isUploading, setIsUploading] = useState(false);
  const { isGlobalUploading, setIsGlobalUploading } = useEddUploadContext();
  const isBusy = isUploading || Boolean(isGlobalUploading);
  const [rowResult, setRowResult] = useState(null);

  const showRowResultToaster = (rowResultData, action) =>
    triggerToaster(...getRowResultToast(rowResultData, action));

  const handleDownloadTemplate = async () => {
    const result = await downloadTemplateFile(
      EDD_TEMPLATE_NAMES.VENDOR_WORKING_HOURS,
    );
    if (result && !result.success) {
      triggerToaster(result.error || "Failed to download template", "error");
    }
  };

  // keepRows: failed edits kept on screen so user can fix them
  const loadSchedule = async (vendorId, keepRows = []) => {
    setIsScheduleLoading(true);
    const result = await fetchVendorWorkingHours(vendorId);
    if (result.error) triggerToaster(result.error, "error");
    const freshSchedule = buildWeeklySchedule(result.data);
    setSavedSchedule(freshSchedule);
    setSchedule(
      freshSchedule.map((row) => {
        const keptRow = keepRows.find((r) => r.day === row.day);
        return keptRow ? { ...keptRow, id: row.id } : row;
      }),
    );
    setIsScheduleLoading(false);
  };

  const handleFileUpload = async (file) => {
    if (isBusy) return false;
    setIsUploading(true);
    setIsGlobalUploading?.(true);
    try {
      const result = await uploadVendorWorkingHoursFile(vendor.vendor_id, file);
      if (result.error) {
        triggerToaster(result.error, "error");
        return false;
      }
      showRowResultToaster(result.data, "Upload");
      if (result.data.failedCount) {
        setRowResult({ title: "Bulk upload failed rows", ...result.data });
      }
      if (result.data.successCount) loadSchedule(vendor.vendor_id);
      return true;
    } finally {
      setIsUploading(false);
      setIsGlobalUploading?.(false);
    }
  };

  const handleVendorChange = (option) => {
    setVendor(option);
    setRowResult(null);
    if (!option) {
      setSchedule([]);
      setSavedSchedule([]);
      return;
    }
    loadSchedule(option.vendor_id);
  };

  const handleRowChange = (day, field, value) => {
    setSchedule((prev) =>
      prev.map((row) => (row.day === day ? { ...row, [field]: value } : row)),
    );
  };

  const handleRowReset = (day) => {
    const savedRow = savedSchedule.find((row) => row.day === day);
    setSchedule((prev) =>
      prev.map((row) => (row.day === day ? { ...savedRow } : row)),
    );
  };

  const handleDiscard = () => {
    setSchedule(savedSchedule.map((row) => ({ ...row })));
  };

  const handleSave = async () => {
    if (schedule.some((row) => validateScheduleRow(row))) {
      triggerToaster("Please fix the highlighted rows", "error");
      return;
    }
    const changedRows = getChangedScheduleRows(schedule, savedSchedule);
    if (!changedRows.length) {
      triggerToaster("No changes to save", "info");
      return;
    }
    setIsSaving(true);
    const result = await saveVendorWorkingHours(vendor.vendor_id, changedRows);
    setIsSaving(false);

    if (result.error) {
      triggerToaster(result.error, "error");
      return;
    }
    const { successCount, failedCount, failedRows } = result.data;
    showRowResultToaster(result.data, "Save schedule");
    setRowResult(
      failedCount
        ? { title: "Save schedule failed rows", ...result.data }
        : null,
    );
    if (!successCount) return;
    // rowNumber is 1 based position in the payload
    const failedEdits = failedRows
      .map((row) => changedRows[row.rowNumber - 1])
      .filter(Boolean);
    loadSchedule(vendor.vendor_id, failedEdits);
  };

  return (
    <Box>
      <Box mb={6}>
        <Typography fontSize={18} fontWeight="bold">
          Vendor Working Hours
        </Typography>
        <Typography fontSize={14} color="text.secondary">
          Weekly baseline operating hours. Pick a vendor to view or update its
          weekly schedule — days without an active row are treated as
          non-working.
        </Typography>
      </Box>

      <EddSectionCard>
        <Grid container spacing={8}>
          <Grid item xs={12} md={7}>
            <VendorSearch vendor={vendor} onVendorChange={handleVendorChange} />
          </Grid>
          <Grid item xs={12} md={5}>
            <FileDropzone
              compact
              title="Bulk upload"
              acceptedTypes={["csv"]}
              maxSizeMB={5}
              onUpload={handleFileUpload}
              onDownloadTemplate={handleDownloadTemplate}
              isUploading={isUploading}
              disabled={!vendor || isBusy}
              helperText={!vendor ? "Select a vendor first" : undefined}
              hint={UPLOAD_FORMAT_HINT}
            />
          </Grid>
        </Grid>
        {vendor && (
          <VendorInfoCard
            vendor={vendor}
            stats={[
              {
                label: "Weekly",
                value: `${getWeeklyTotalHours(savedSchedule).toFixed(1)}h`,
              },
            ]}
          />
        )}
        <RowResultDialog
          open={Boolean(rowResult)}
          title={rowResult?.title ?? ""}
          result={rowResult}
          onClose={() => setRowResult(null)}
        />
      </EddSectionCard>

      {vendor && (
        <EddSectionCard title="Weekly Schedule">
          <WeeklySchedule
            schedule={schedule}
            savedSchedule={savedSchedule}
            isLoading={isScheduleLoading}
            isSaving={isSaving}
            onRowChange={handleRowChange}
            onRowReset={handleRowReset}
            onDiscard={handleDiscard}
            onSave={handleSave}
          />
        </EddSectionCard>
      )}
    </Box>
  );
};

export default VendorWorkingHours;
